import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';
import { createRoot } from 'react-dom/client';
import api from '../../../services/api';
import './MentionSuggestion.css';

/**
 * Build TipTap mention suggestion configuration.
 *
 * @param {string|null} projectId
 * @param {function|null} onMention - callback when a user is mentioned
 * @returns {object} suggestion config for @tiptap/extension-mention
 */
export function buildMentionSuggestion(projectId, onMention) {
  let debounceTimer = null;

  return {
    char: '@',

    items: ({ query }) => {
      return new Promise((resolve) => {
        if (debounceTimer) clearTimeout(debounceTimer);

        if (!projectId) {
          resolve([]);
          return;
        }

        debounceTimer = setTimeout(async () => {
          try {
            const response = await api.get(`/projects/${projectId}/users/search`, {
              params: { q: query, limit: 8 },
            });
            resolve(response.data.users || []);
          } catch (error) {
            console.error('Error searching users:', error);
            resolve([]);
          }
        }, 200);
      });
    },

    render: () => {
      let popup = null;
      let root = null;
      let listRef = { current: null };

      return {
        onStart: (props) => {
          popup = document.createElement('div');
          popup.className = 'mention-suggestion-portal';
          document.body.appendChild(popup);

          root = createRoot(popup);
          root.render(
            <MentionList
              ref={listRef}
              items={props.items}
              command={props.command}
              onMention={onMention}
            />
          );

          updatePosition(popup, props.clientRect);
        },

        onUpdate: (props) => {
          if (root) {
            root.render(
              <MentionList
                ref={listRef}
                items={props.items}
                command={props.command}
                onMention={onMention}
              />
            );
          }
          if (popup) {
            updatePosition(popup, props.clientRect);
          }
        },

        onKeyDown: (props) => {
          if (listRef.current) {
            return listRef.current.onKeyDown(props.event);
          }
          return false;
        },

        onExit: () => {
          if (root) {
            root.unmount();
            root = null;
          }
          if (popup) {
            popup.remove();
            popup = null;
          }
        },
      };
    },
  };
}

/**
 * Position the mention popup near the cursor
 */
function updatePosition(popup, clientRect) {
  if (!clientRect) return;
  const rect = typeof clientRect === 'function' ? clientRect() : clientRect;
  if (!rect) return;

  const viewportHeight = window.innerHeight;
  const dropdownHeight = 280;

  let top = rect.bottom + 6;
  if (top + dropdownHeight > viewportHeight - 20) {
    top = rect.top - dropdownHeight - 6;
  }

  let left = Math.max(10, Math.min(rect.left, window.innerWidth - 310));

  popup.style.position = 'fixed';
  popup.style.top = `${Math.max(10, top)}px`;
  popup.style.left = `${left}px`;
  popup.style.zIndex = '99999';
}

/**
 * Mention dropdown list component
 */
const MentionList = forwardRef(({ items, command, onMention }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector('.mention-item.selected');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const selectItem = useCallback(
    (index) => {
      const user = items[index];
      if (user) {
        command({ id: user.id, label: user.full_name });
        if (onMention) onMention(user);
      }
    },
    [items, command, onMention]
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
        return true;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (items.length > 0) {
          selectItem(selectedIndex);
        }
        return true;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        return true;
      }
      return false;
    },
  }));

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (!items || items.length === 0) {
    return (
      <div className="mention-dropdown">
        <div className="mention-empty">No users found</div>
      </div>
    );
  }

  return (
    <div className="mention-dropdown" ref={listRef}>
      <ul className="mention-list">
        {items.map((user, index) => (
          <li
            key={user.id}
            className={`mention-item ${index === selectedIndex ? 'selected' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectItem(index);
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <div className="mention-avatar">
              <span className="mention-initials">
                {getInitials(user.first_name, user.last_name)}
              </span>
            </div>
            <div className="mention-info">
              <span className="mention-name">{user.full_name}</span>
              <span className="mention-email">{user.email}</span>
            </div>
            <span className={`mention-role role-${user.role}`}>{user.role}</span>
          </li>
        ))}
      </ul>
    </div>
  );
});

MentionList.displayName = 'MentionList';

export default MentionList;
